<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { onClickOutside } from '@vueuse/core'
import { useTranslations } from '@/composables/useTranslations.ts'
const { t } = useTranslations()

const props = defineProps<{ open: boolean; imageUrl: string | null }>()
const emit = defineEmits(['close', 'save'])

const modalRef = ref(null)
const imageRef = ref<HTMLImageElement | null>(null)
let cropper: Cropper | null = null

const initCropper = () => {
  cropper?.destroy()
  cropper = null

  if (props.open && props.imageUrl && imageRef.value) {
    cropper = new Cropper(imageRef.value, {
      aspectRatio: 1,
      viewMode: 1,
    })
  }
}

onMounted(initCropper)
watch(() => [props.open, props.imageUrl], initCropper, { flush: 'post' })

onBeforeUnmount(() => {
  cropper?.destroy()
  cropper = null
})

const getCroppedImage = (ev: Event) => {
  ev.preventDefault()
  ev.stopPropagation()

  const croppedCanvas = cropper?.getCroppedCanvas()
  if (!croppedCanvas) {
    return
  }

  const size = Math.min(croppedCanvas.width, croppedCanvas.height)
  const circleCanvas = document.createElement('canvas')
  circleCanvas.width = size
  circleCanvas.height = size

  const ctx = circleCanvas.getContext('2d')
  if (ctx) {
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()
    ctx.drawImage(
      croppedCanvas,
      (croppedCanvas.width - size) / 2,
      (croppedCanvas.height - size) / 2,
      size,
      size,
      0,
      0,
      size,
      size
    )
  }

  emit('save', circleCanvas.toDataURL())
}

onClickOutside(modalRef, () => {
  emit('close')
})
</script>

<template>
  <div v-if="open" class="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-1000">
    <slot name="loader"></slot>
    <div ref="modalRef" class="bg-white rounded-lg p-6 shadow-lg w-96">
      <h2 class="text-lg font-semibold mb-4">{{ t('settings.primary.cropImage') }}</h2>

      <div v-if="imageUrl" class="w-full h-64">
        <img ref="imageRef" :src="imageUrl" alt="" class="block max-w-full" />
      </div>

      <div class="flex justify-end gap-2 mt-4">
        <button @click="$emit('close')" class="bg-gray-300 px-4 py-2 rounded-sm">
          {{ t('common.buttons.cancel') }}
        </button>
        <button @click="(ev) => getCroppedImage(ev)" class="bg-indigo-600 text-white px-4 py-2 rounded-sm">
          {{ t('common.buttons.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
::v-deep(.cropper-view-box) {
  border-radius: 50%;
  outline: 2px solid #39f;
  overflow: hidden;
}

::v-deep(.cropper-face) {
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3) !important;
}

::v-deep(.cropper-dashed) {
  display: none;
}

::v-deep(.cropper-view-box::before),
::v-deep(.cropper-view-box::after) {
  display: none;
}
</style>
